import React, { useEffect, useMemo, useState } from 'react';
import { fetchAgentWhatsAppChats, AgentWhatsAppChat, AgentWhatsAppMessage } from '../services/api';

// Super-admin-only testing page. Pulls a chrome-extension-synced WhatsApp
// mirror for a single agent device and renders it in a WhatsApp-like
// two-pane layout. Agent id is hardcoded while in testing — swap to a
// selector when going GA.
const AGENT_ID = 2;

// Decide outgoing vs incoming. Primary signal is `direction`, secondary is
// `sender` — the API's own sample uses `sender: "AGENT"` to mark messages
// the extension sent, while real synced chats sometimes carry a human name
// as sender regardless of who sent it. Treat anything that LOOKS outgoing
// as outgoing; default to incoming.
function isOutgoing(m: AgentWhatsAppMessage): boolean {
  const d = String(m.direction || '').toLowerCase();
  if (d === 'outgoing' || d === 'outbound' || d === 'out' || d === 'sent') return true;

  const sender = String(m.sender || '').toUpperCase();
  if (sender === 'AGENT' || sender === 'ME' || sender === 'SELF' || sender === 'YOU') return true;

  // Chrome-extension variants that may slip through as untyped keys on the
  // response object. Safe-cast to any to avoid breaking the typed contract.
  const anyM = m as any;
  if (anyM.from_me === true || anyM.fromMe === true || anyM.is_from_me === true) return true;

  return false;
}

// Pull the sender/customer name from the first incoming message. Falls back
// through chat-level fields when no incoming message carries a sender.
// This exists because the sync layer sometimes puts a label like
// "Profile details" / "Chats" in customer_name and the actual person's
// name ends up in customer_phone or on individual message rows.
function deriveChatName(chat: AgentWhatsAppChat): string {
  const incomingWithSender = (chat.messages ?? []).find(
    m => !isOutgoing(m) && m.sender && m.sender.trim() !== ''
  );
  if (incomingWithSender?.sender) return incomingWithSender.sender.trim();

  const anyWithSender = (chat.messages ?? []).find(
    m => m.sender && m.sender.trim() !== '' && m.sender.toUpperCase() !== 'AGENT'
  );
  if (anyWithSender?.sender) return anyWithSender.sender.trim();

  // Chat-level fallbacks. Skip the known junk labels the sync layer emits.
  const junk = new Set(['profile details', 'chats', 'profile', 'details', '']);
  const name = (chat.customer_name || '').trim();
  if (name && !junk.has(name.toLowerCase())) return name;

  const phone = (chat.customer_phone || '').trim();
  if (phone) return phone;

  return chat.chat_id;
}

// customer_phone is sometimes a real phone (+91..., digits), sometimes a
// human name (sync-layer field confusion). Only treat it as a phone when it
// actually looks like one, so we can decide whether to show it as the
// header subtitle.
function looksLikePhone(s: string | null | undefined): boolean {
  if (!s) return false;
  const trimmed = s.trim();
  if (trimmed.length < 7) return false;
  // Allow leading +, digits, spaces, dashes, parens. If stripping those
  // leaves <7 digits, it's not a phone.
  const digits = trimmed.replace(/[^0-9]/g, '');
  return digits.length >= 7;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function fmtDayHeader(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return 'Today';
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const AgentWhatsAppPage: React.FC = () => {
  const [chats, setChats] = useState<AgentWhatsAppChat[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAgentWhatsAppChats(AGENT_ID)
      .then((res) => {
        if (cancelled) return;
        const list = res.chats ?? [];
        setChats(list);
        if (list.length > 0) setSelectedId(list[0].chat_id);
        setError(null);
      })
      .catch((e) => !cancelled && setError(e?.message || 'Failed to load chats'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter(c =>
      (c.customer_name || '').toLowerCase().includes(q)
      || (c.customer_phone || '').toLowerCase().includes(q)
      || (c.last_message?.text || '').toLowerCase().includes(q)
    );
  }, [chats, query]);

  const selected = chats.find(c => c.chat_id === selectedId) ?? null;

  // Group messages by calendar day so we can render WhatsApp-style
  // "Today / Yesterday / 12 Oct" date separators between them.
  const dayGroups = useMemo(() => {
    if (!selected?.messages?.length) return [];
    const sorted = [...selected.messages].sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    const groups: { label: string; messages: AgentWhatsAppMessage[] }[] = [];
    for (const m of sorted) {
      const label = fmtDayHeader(m.timestamp);
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.messages.push(m);
      else groups.push({ label, messages: [m] });
    }
    return groups;
  }, [selected]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Agent WhatsApp</h1>
        <span style={styles.badge}>Testing · agent_id={AGENT_ID}</span>
      </div>

      <div style={styles.layout}>
        {/* ─── Chat list (left) ───────────────────────────────── */}
        <aside style={styles.listPane}>
          <div style={styles.listHeader}>
            <input
              type="text"
              placeholder="Search or start new chat"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={styles.search}
            />
          </div>

          <div style={styles.chatList}>
            {loading && <div style={styles.muted}>Loading chats…</div>}
            {error && <div style={styles.error}>{error}</div>}
            {!loading && !error && filtered.length === 0 && (
              <div style={styles.muted}>No chats.</div>
            )}

            {filtered.map(c => {
              const active = c.chat_id === selectedId;
              const name = deriveChatName(c);
              const preview = c.last_message?.text || '';
              const when = c.last_message?.timestamp ? fmtTime(c.last_message.timestamp) : '';
              const lastOut = c.last_message ? isOutgoing(c.last_message) : false;
              return (
                <button
                  key={c.chat_id}
                  type="button"
                  onClick={() => setSelectedId(c.chat_id)}
                  style={{ ...styles.chatRow, ...(active ? styles.chatRowActive : {}) }}
                >
                  <div style={styles.avatar}>{initials(name)}</div>
                  <div style={styles.chatMeta}>
                    <div style={styles.chatTopLine}>
                      <span style={styles.chatName}>{name}</span>
                      <span style={styles.chatTime}>{when}</span>
                    </div>
                    <div style={styles.chatBottomLine}>
                      <span style={styles.chatPreview}>
                        {lastOut && <span style={styles.previewPrefix}>You: </span>}
                        {preview}
                      </span>
                      {c.unread_count > 0 && (
                        <span style={styles.unreadPill}>{c.unread_count}</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* ─── Message pane (right) ───────────────────────────── */}
        <section style={styles.messagePane}>
          {!selected && (
            <div style={styles.emptyPane}>
              <div style={styles.emptyIcon}>💬</div>
              <div>Select a chat on the left to view messages.</div>
            </div>
          )}

          {selected && (
            <>
              {(() => {
                const chatName = deriveChatName(selected);
                const phone = looksLikePhone(selected.customer_phone) ? selected.customer_phone : null;
                return (
                  <div style={styles.messageHeader}>
                    <div style={styles.avatarGreen}>{initials(chatName)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.messageHeaderName}>{chatName}</div>
                      {phone && (
                        <div style={styles.messageHeaderPhone}>{phone}</div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div style={styles.messageList}>
                {dayGroups.length === 0 && (
                  <div style={styles.muted}>No messages in this chat.</div>
                )}
                {dayGroups.map((group, gi) => (
                  <React.Fragment key={gi}>
                    <div style={styles.daySeparatorRow}>
                      <span style={styles.daySeparator}>{group.label}</span>
                    </div>
                    {group.messages.map(m => {
                      const out = isOutgoing(m);
                      // Hover tooltip shows the raw direction / sender the
                      // server returned — useful for debugging a chat where
                      // every message lands on the wrong side.
                      const debugHint =
                        'direction=' + (m.direction ?? '(null)') +
                        ' · sender=' + (m.sender ?? '(null)');
                      return (
                        <div
                          key={m.message_id}
                          style={{
                            ...styles.bubbleRow,
                            justifyContent: out ? 'flex-end' : 'flex-start',
                          }}
                          title={debugHint}
                        >
                          <div style={{
                            ...styles.bubble,
                            ...(out ? styles.bubbleOut : styles.bubbleIn),
                          }}>
                            <div style={styles.bubbleText}>{m.text}</div>
                            <div style={styles.bubbleTime}>
                              {fmtTime(m.timestamp)}
                              {out && <span style={styles.ticks}> ✓✓</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p.charAt(0).toUpperCase()).join('') || '?';
}

// WhatsApp-ish palette:
//   #075e54  header dark green
//   #128c7e  action green
//   #25d366  bright green (unread pill)
//   #dcf8c6  outgoing bubble
//   #ffffff  incoming bubble
//   #ece5dd  chat wallpaper (we use a slightly lighter beige for contrast)
const styles: Record<string, React.CSSProperties> = {
  page:        { padding: 24, display: 'flex', flexDirection: 'column', gap: 16, height: 'calc(100vh - 48px)' },
  header:      { display: 'flex', alignItems: 'center', gap: 12 },
  title:       { margin: 0, fontSize: 20, fontWeight: 600 },
  badge:       { background: '#fef3c7', color: '#92400e', padding: '2px 10px', borderRadius: 999, fontSize: 12 },

  layout:      { display: 'grid', gridTemplateColumns: '360px 1fr', gap: 0, flex: 1, minHeight: 0, border: '1px solid #d1d7db', borderRadius: 8, overflow: 'hidden', background: '#fff' },

  // --- Left pane: chat list ---
  listPane:    { display: 'flex', flexDirection: 'column', borderRight: '1px solid #d1d7db', minHeight: 0, background: '#fff' },
  listHeader:  { padding: '10px 12px', background: '#f0f2f5', borderBottom: '1px solid #d1d7db' },
  search:      { width: '100%', boxSizing: 'border-box', padding: '8px 14px', border: 'none', borderRadius: 20, fontSize: 14, outline: 'none', background: '#fff' },
  chatList:    { overflowY: 'auto', display: 'flex', flexDirection: 'column', flex: 1 },
  chatRow:     { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid #f0f2f5', textAlign: 'left', cursor: 'pointer' },
  chatRowActive: { background: '#f0f2f5' },
  avatar:      { width: 44, height: 44, borderRadius: '50%', background: '#128c7e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 600, flexShrink: 0 },
  avatarGreen: { width: 40, height: 40, borderRadius: '50%', background: '#128c7e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flexShrink: 0 },
  chatMeta:    { flex: 1, minWidth: 0 },
  chatTopLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 },
  chatName:    { fontWeight: 500, fontSize: 15, color: '#111b21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chatTime:    { fontSize: 12, color: '#667781', flexShrink: 0 },
  chatBottomLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 2 },
  chatPreview: { fontSize: 13, color: '#667781', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
  previewPrefix: { color: '#667781' },
  unreadPill:  { background: '#25d366', color: '#fff', fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 999, minWidth: 18, textAlign: 'center' },

  // --- Right pane: messages ---
  messagePane: { display: 'flex', flexDirection: 'column', minHeight: 0, background: '#efeae2' },
  emptyPane:   { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#667781', gap: 12 },
  emptyIcon:   { fontSize: 48, opacity: 0.4 },
  messageHeader: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: '#f0f2f5', borderBottom: '1px solid #d1d7db' },
  messageHeaderName:  { fontWeight: 500, fontSize: 16, color: '#111b21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  messageHeaderPhone: { fontSize: 13, color: '#667781', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  messageList: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 60px', backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'40\' height=\'40\'><circle cx=\'20\' cy=\'20\' r=\'1\' fill=\'%23d9d2c7\'/></svg>")' },

  daySeparatorRow: { display: 'flex', justifyContent: 'center', margin: '8px 0' },
  daySeparator:    { background: '#e1f2fb', color: '#54656f', fontSize: 12, padding: '4px 12px', borderRadius: 8, boxShadow: '0 1px 1px rgba(0,0,0,0.05)' },

  bubbleRow:   { display: 'flex' },
  bubble:      { maxWidth: '65%', padding: '6px 10px', borderRadius: 8, boxShadow: '0 1px 1px rgba(0,0,0,0.08)', position: 'relative' },
  bubbleIn:    { background: '#ffffff', color: '#111b21', borderTopLeftRadius: 0 },
  bubbleOut:   { background: '#d9fdd3', color: '#111b21', borderTopRightRadius: 0 },
  bubbleText:  { fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 },
  bubbleTime:  { fontSize: 11, color: '#667781', marginTop: 2, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 4 },
  ticks:       { color: '#53bdeb' },

  muted:       { color: '#667781', fontSize: 13, padding: 20, textAlign: 'center' },
  error:       { color: '#b91c1c', fontSize: 13, padding: 20, textAlign: 'center' },
};

export default AgentWhatsAppPage;
